import {
  Box,
  Center,
  Flex,
  Icon,
  IconButton,
  Tag,
  Text,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useHoverRef from "../../hooks/useHoverRef";
import useMobile from "../../hooks/useMobile";
import { ArrowLeft, ArrowRight, ExternalShare } from "../icons/icons";
import { formatDuration } from "../../pages/host/EventsEvents";

import { LazyLoadedImage } from "../lazy/LazyLoadedImage";

const EventGalleryCard = (props: {
  h?: string;

  thumbnails: string[];
  thumbnailWidth: number | undefined;

  hourlyPrice?: number;
  shortestDuration?: number;

  onClick?: () => void;
  isClickable?: boolean;
  hasBackButton?: boolean;

  isMultiple?: boolean;

  borderRadius?: string;
  onShare?: () => void;
  hasShareButton?: boolean;
}) => {
  const navigate = useNavigate();

  const [thumbnailIndex, setThumbnailIndex] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);

  const [isHovered, hoverRef] = useHoverRef<HTMLDivElement>();

  const isMobile = useMobile(600);
  const touchStartX = useRef(0);

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isSwiping) {
      setIsSwiping(true);

      const deltaX = e.touches[0].clientX - touchStartX.current;
      const threshold = 5; // Adjust this value based on your needs

      if (deltaX > threshold && thumbnailIndex > 0) {
        setThumbnailIndex(thumbnailIndex - 1);
      } else if (
        deltaX < -threshold &&
        thumbnailIndex < props.thumbnails.length - 1
      ) {
        setThumbnailIndex(thumbnailIndex + 1);
      }
    }
  };

  const handleTouchEnd = () => {
    setIsSwiping(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const imageHeight = props.h;
  const imageWidth = props.thumbnailWidth ?? 0;

  return (
    <Box
      h={props.h}
      w="100%"
      bg="#DCE2EA"
      borderRadius={props.borderRadius ?? "10px"}
      position="relative"
      overflow="hidden"
      ref={hoverRef}
    >
      <Flex
        left={`-${thumbnailIndex * (props.thumbnailWidth ?? 0)}px`}
        transition="0.2s ease-in"
        position="absolute"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {props.thumbnails.map((thumbnail: string, i: number) => {
          return (
            <LazyLoadedImage
              key={`thumbnail-${i}`}
              height={imageHeight}
              width={imageWidth}
              src={thumbnail}
              zIndex={0}
            />
          );
        })}
      </Flex>

      <Flex pos="absolute" right="15px" top="15px" gap="10px">
        {props.hourlyPrice ? (
          <Tag variant="shadow" size="lg" zIndex="1">
            {formatDuration(new Date(), new Date(Date.now() + props.shortestDuration! * 60000))} from
            <Text variant="card" fontSize="sm" ml="5px">
              ${props.hourlyPrice}
            </Text>
          </Tag>
        ) : (
          <></>
        )}
        {props.hasShareButton ? (
          <Tag
            variant="shadow"
            maxW="32px"
            p="8px"
            cursor="pointer"
            onClick={props.onShare}
            size="lg"
            zIndex="1"
          >
            <Icon
              w="16px"
              h="16px"
              viewBox="0 0 16 16"
              fill="none"
              color="white"
            >
              <ExternalShare />
            </Icon>
          </Tag>
        ) : (
          <></>
        )}
      </Flex>

      {props.thumbnails.length > 1 && (
        <Center w="100%" pos="absolute" bottom="15px" gap="8px">
          {props.thumbnails.map((thumbnail: string, i: number) => (
            <Box
              cursor={props.isClickable ? "pointer" : "default"}
              onClick={() => setThumbnailIndex(i)}
              key={`stepper-${thumbnail}-${i}`}
              bg="#FFFFFF"
              w="8px"
              h="8px"
              borderRadius="4px"
              opacity={thumbnailIndex === i ? "1" : "0.5"}
            />
          ))}
        </Center>
      )}

      {props.hasBackButton ? (
        <IconButton
          bg="#F5F7F933"
          position="absolute"
          size="sm"
          borderRadius="17px"
          left="15px"
          top="15px"
          cursor="pointer"
          onClick={() => navigate("/user/play")}
          _hover={{}}
          _active={{}}
          aria-label="previous page"
          zIndex={1}
          icon={
            <Icon width="23px" height="20px" viewBox="0 0 23 20">
              <ArrowLeft color="white" />
            </Icon>
          }
        />
      ) : (
        <></>
      )}

      {isMobile ? (
        <></>
      ) : (
        <>
          {props.thumbnails.length > 1 && (
            <>
              <IconButton
                opacity={isHovered ? "1" : "0"}
                transition="0.2s ease-out"
                _disabled={{ opacity: isHovered ? "1" : "0" }}
                isDisabled={thumbnailIndex === 0}
                onClick={(e: any) => {
                  e.stopPropagation();
                  setThumbnailIndex(thumbnailIndex - 1);
                }}
                bg="white"
                position="absolute"
                size="sm"
                borderRadius="17px"
                left="15px"
                top="45%"
                cursor="pointer"
                aria-label="previous image"
                icon={
                  <Icon width="23px" height="20px" viewBox="0 0 23 20">
                    <ArrowLeft color="#171C22" />
                  </Icon>
                }
              />
              <IconButton
                opacity={isHovered ? "1" : "0"}
                transition="0.2s ease-out"
                _disabled={{ opacity: isHovered ? "1" : "0" }}
                isDisabled={thumbnailIndex === props.thumbnails.length - 1}
                onClick={(e: any) => {
                  e.stopPropagation();
                  setThumbnailIndex(thumbnailIndex + 1);
                }}
                bg="white"
                position="absolute"
                size="sm"
                borderRadius="17px"
                right="15px"
                top="45%"
                cursor="pointer"
                aria-label="previous image"
                icon={
                  <Icon width="23px" height="20px" viewBox="0 0 23 20">
                    <ArrowRight color="#171C22" />
                  </Icon>
                }
              />
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default EventGalleryCard;
